"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { LasztiLogo } from "@/components/site/laszti-logo";

const leagueLinks = [
  { label: "Premier League", href: "/premier-league" },
  { label: "La Liga", href: "/la-liga" },
  { label: "Serie A", href: "/serie-a" },
  { label: "Bundesliga", href: "/bundesliga" },
  { label: "Bajnokok Ligája", href: "/champions-league" },
  { label: "Európa Liga", href: "/europa-liga" },
];

const communityLinks = [
  { label: "Facebook", href: "https://www.facebook.com", external: true },
  { label: "Instagram", href: "https://www.instagram.com", external: true },
  { label: "Fórum", href: "/forum" },
  { label: "Bejelentkezés", href: "/login" },
];

const legalLinks = [
  { label: "Adatvédelmi tájékoztató", href: "/privacy" },
  { label: "Felhasználási feltételek", href: "/terms" },
  { label: "Kapcsolat / Impresszum", href: "/contact" },
];

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      title={label}
      target="_blank"
      rel="noreferrer"
      className="grid h-10 w-10 place-items-center rounded-full border border-[#D8973C]/40 text-[#D8973C] transition hover:bg-[#D8973C] hover:text-[#273E47]"
    >
      {children}
    </Link>
  );
}

function FacebookIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-current">
      <path d="M13.5 21v-7.2H16l.4-2.8h-2.9V9.2c0-.8.3-1.6 1.7-1.6h1.4V5.2s-1.3-.2-2.5-.2c-2.5 0-4.1 1.5-4.1 4.3V11H7.5v2.8H10V21h3.5Z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-current">
      <path d="M7.8 3h8.4A4.8 4.8 0 0 1 21 7.8v8.4a4.8 4.8 0 0 1-4.8 4.8H7.8A4.8 4.8 0 0 1 3 16.2V7.8A4.8 4.8 0 0 1 7.8 3Zm0 1.7A3.1 3.1 0 0 0 4.7 7.8v8.4a3.1 3.1 0 0 0 3.1 3.1h8.4a3.1 3.1 0 0 0 3.1-3.1V7.8a3.1 3.1 0 0 0-3.1-3.1H7.8Zm8.9 1.3a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 7.1a4.9 4.9 0 1 1 0 9.8 4.9 4.9 0 0 1 0-9.8Zm0 1.7a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4Z" />
    </svg>
  );
}

export function SiteFooter() {
  const pathname = usePathname();

  if (pathname?.startsWith("/admin")) return null;

  return (
    <footer className="mt-auto shrink-0 bg-[#273E47] text-[#F7F5EF]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-6">
        <div className="grid gap-6 pb-8 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
          <div className="hidden lg:block" />

          <div className="flex justify-center">
            <Link href="/" aria-label="Laszti365 főoldal" className="inline-flex items-center justify-center">
              <LasztiLogo className="w-[190px] sm:w-[220px]" priority={false} />
            </Link>
          </div>

          <div className="flex items-center justify-center gap-3 lg:justify-end">
            <SocialIcon href="https://www.facebook.com" label="Facebook">
              <FacebookIcon />
            </SocialIcon>
            <SocialIcon href="https://www.instagram.com" label="Instagram">
              <InstagramIcon />
            </SocialIcon>
          </div>
        </div>

        <div className="border-t border-[#D8973C]/20 pt-8">
          <p className="mb-6 text-sm font-bold leading-6 text-[#F7F5EF]">
            Laszti365
          </p>
          <div className="grid gap-10 sm:grid-cols-3 lg:max-w-4xl lg:gap-16">
            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.24em] text-[#D8973C]">Ligák</h2>
              <nav className="mt-4 grid gap-2.5 text-sm font-semibold">
                {leagueLinks.map((link) => (
                  <Link key={link.href} href={link.href} className="transition hover:text-[#D8973C]">
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.24em] text-[#D8973C]">Közösség</h2>
              <nav className="mt-4 grid gap-2.5 text-sm font-semibold">
                {communityLinks.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noreferrer" : undefined}
                    className="transition hover:text-[#D8973C]"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.24em] text-[#D8973C]">Jogi</h2>
              <nav className="mt-4 grid gap-2.5 text-sm font-semibold">
                {legalLinks.map((link) => (
                  <Link key={link.href} href={link.href} className="transition hover:text-[#D8973C]">
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
